'use strict';

// ---------- Motor de ejecución del flujo DTMF ----------
//
// Port 1:1 de src/flow-engine.js (Electron). Única diferencia real: usa
// TinyEmitter en vez de events.EventEmitter de Node. La lógica de cada nodo,
// el auto-arranque y sus comentarios se mantienen igual que el original.

import { TinyEmitter } from './tiny-emitter.js';

const DEFAULT_WAIT_MS = 8000;
const VENTANA_TIMBRE_MS = 20000;

export class FlowEngine extends TinyEmitter {
  constructor({ audioBridge, dtmf, phoneBridge, recordingsDir }) {
    super();
    this.audioBridge = audioBridge;
    this.dtmf = dtmf;
    this.phoneBridge = phoneBridge;
    this.recordingsDir = recordingsDir || null;
    this.flow = { nodes: [], edges: [] };
    this.running = false;
    this._stopRequested = false;
    this._ultimoTimbre = null;
    this._cleanupCurrentWait = null;
  }

  setFlow(flow) {
    this.flow = flow;
  }

  isRunning() {
    return this.running;
  }

  startsWithAnswer() {
    const startNode = this.flow.nodes.find((n) => n.type === 'start');
    if (!startNode) return false;
    const firstId = this._nextFrom(startNode.id, 'out');
    const first = this.flow.nodes.find((n) => n.id === firstId);
    return Boolean(first && first.type === 'answer');
  }

  async run() {
    if (this.running) return;
    const startNode = this.flow.nodes.find((n) => n.type === 'start');
    if (!startNode) {
      this.emit('log', 'El flujo no tiene nodo de Inicio.');
      return;
    }
    this.running = true;
    this._stopRequested = false;
    this.emit('status', { running: true });
    this.emit('log', 'Flujo iniciado.');

    try {
      await this._execFrom(startNode.id);
    } catch (err) {
      this.emit('log', `Error: ${err.message}`);
    }

    this.running = false;
    this.emit('active-node', null);
    this.emit('status', { running: false });
    this.emit('log', 'Flujo terminado.');
  }

  stop() {
    if (!this.running) return;
    this._stopRequested = true;
    if (this._cleanupCurrentWait) this._cleanupCurrentWait();
    this.audioBridge.stopFile().catch(() => {});
    if (this.audioBridge.stopRecording) this.audioBridge.stopRecording();
  }

  async _execFrom(nodeId) {
    let currentId = nodeId;
    while (!this._stopRequested && currentId) {
      const node = this.flow.nodes.find((n) => n.id === currentId);
      if (!node) break;
      this.emit('active-node', node.id);
      currentId = await this._runNode(node);
    }
  }

  async _runNode(node) {
    switch (node.type) {
      case 'start':
        return this._nextFrom(node.id, 'out');

      case 'play': {
        const file = node.data && node.data.file;
        if (!file) {
          this.emit('log', 'Nodo "Reproducir audio" sin archivo elegido — se salta.');
          return this._nextFrom(node.id, 'out');
        }
        this.emit('log', `▶ Reproduciendo: ${file.split('/').pop()}`);
        await this._playAndWait(file);
        return this._nextFrom(node.id, 'out');
      }

      case 'wait': {
        const timeoutMs = (node.data && node.data.timeoutMs) || DEFAULT_WAIT_MS;
        const sourceId = (node.data && node.data.sourceId) || this._defaultDtmfSource();
        this.emit('log', `⏳ Esperando una tecla (${Math.round(timeoutMs / 1000)}s)…`);
        const digit = await this._waitForDigit(timeoutMs, sourceId);
        if (this._stopRequested) return null;
        this.emit('log', digit ? `☎ Tecla recibida: ${digit}` : '⏱ Sin respuesta.');
        const port = digit ? `digit:${digit}` : 'timeout';
        return this._nextFrom(node.id, port) || this._nextFrom(node.id, 'other');
      }

      case 'answer':
        this.emit('log', '☎ Contestando…');
        try {
          await this.phoneBridge.answer();
        } catch (err) {
          this.emit('log', `No se pudo contestar: ${err.message}`);
        }
        await this._sleep(1200);
        return this._nextFrom(node.id, 'out');

      case 'pause': {
        const ms = (node.data && node.data.ms) || 1000;
        this.emit('log', `… ${Math.round(ms / 1000)}s de silencio`);
        await this._sleep(ms);
        return this._nextFrom(node.id, 'out');
      }

      case 'record': {
        const seconds = (node.data && node.data.seconds) || 20;
        this.emit('log', `⏺ Grabando mensaje (${seconds}s)…`);
        try {
          const file = await this.audioBridge.recordToFile({
            sourceId: (node.data && node.data.sourceId) || this._defaultDtmfSource(),
            seconds,
            dir: this.recordingsDir,
          });
          this.emit('log', `⏺ Guardado: ${file.split('/').pop()}`);
        } catch (err) {
          this.emit('log', `No se pudo grabar: ${err.message}`);
        }
        return this._nextFrom(node.id, 'out');
      }

      case 'hangup':
        this.emit('log', '☎ Colgando…');
        try {
          await this.phoneBridge.reject();
        } catch (err) {
          this.emit('log', `No se pudo colgar: ${err.message}`);
        }
        return null;

      default:
        this.emit('log', `Tipo de nodo desconocido: ${node.type}`);
        return null;
    }
  }

  _defaultDtmfSource() {
    const status = this.audioBridge.getStatus();
    return (status && status.config && status.config.phoneCaptureSource) || null;
  }

  _sleep(ms) {
    return new Promise((resolve) => {
      const timer = setTimeout(() => {
        this._cleanupCurrentWait = null;
        resolve();
      }, ms);
      this._cleanupCurrentWait = () => {
        clearTimeout(timer);
        this._cleanupCurrentWait = null;
        resolve();
      };
    });
  }

  shouldAutoRun(prevState, nextState) {
    if (nextState === 'ringing') this._ultimoTimbre = Date.now();
    if (prevState === nextState) return false;

    if (nextState === 'offhook') {
      if (prevState === 'ringing') return true;
      if (prevState === null) return true;
      if (this._ultimoTimbre && Date.now() - this._ultimoTimbre < VENTANA_TIMBRE_MS) return true;
      return false;
    }

    if (nextState === 'ringing') return this.startsWithAnswer();
    return false;
  }

  _nextFrom(nodeId, port) {
    const edge = this.flow.edges.find((e) => e.from === nodeId && e.fromPort === port);
    return edge ? edge.to : null;
  }

  _playAndWait(file) {
    return new Promise((resolve, reject) => {
      let unsubscribe = null;
      const cleanup = () => {
        if (unsubscribe) unsubscribe();
        unsubscribe = null;
      };
      unsubscribe = this.audioBridge.setFilePlaybackEndedListener(() => {
        cleanup();
        resolve();
      });
      this.audioBridge.playFile(file).catch((err) => {
        cleanup();
        reject(err);
      });
    });
  }

  _waitForDigit(timeoutMs, sourceId) {
    return new Promise((resolve) => {
      let settled = false;
      const onDigit = ({ digit }) => finish(digit);
      const timer = setTimeout(() => finish(null), timeoutMs);

      const finish = (digit) => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        this.dtmf.off('digit', onDigit);
        this._cleanupCurrentWait = null;
        resolve(digit);
      };

      this._cleanupCurrentWait = () => finish(null);

      if (!this.dtmf.running) {
        try {
          this.dtmf.start(sourceId);
        } catch (err) {
          this.emit('log', `No se pudo escuchar DTMF: ${err.message}`);
          finish(null);
          return;
        }
      }
      this.dtmf.on('digit', onDigit);
    });
  }
}
